import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { selectTheme } from '../store/slices/settingsSlice';

const ColorModeContext = createContext({ isDark: false, mode: 'light' });

export const useColorMode = () => useContext(ColorModeContext);

function getSystemPrefersDark() {
    return typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : false;
}

// themeMode: 'light' | 'dark' | 'system' — lives in Redux (settingsSlice), persisted to
// localStorage there. 'system' resolves against the OS/browser color-scheme preference,
// tracked live via a matchMedia listener.
export const ThemeProvider = ({ children }) => {
    const themeMode = useSelector(selectTheme);
    const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

    useEffect(() => {
        if (!window.matchMedia) return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const listener = (e) => setSystemPrefersDark(e.matches);
        mql.addEventListener('change', listener);
        return () => mql.removeEventListener('change', listener);
    }, []);

    const isDark = themeMode === 'system' ? systemPrefersDark : themeMode === 'dark';
    const mode = isDark ? 'dark' : 'light';

    // Apply/remove .dark class to document root
    useEffect(() => {
        if (isDark) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDark]);

    const colorMode = useMemo(() => ({ isDark, mode }), [isDark, mode]);

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    ...(mode === 'light'
                        ? {
                            // Light mode colors
                            primary: {
                                main: '#03989F',
                                light: '#35a8ae',
                                dark: '#026b6f',
                                contrastText: '#ffffff',
                            },
                            background: {
                                default: '#efefef',
                                paper: '#ffffff',
                            },
                            text: {
                                primary: '#333333',
                                secondary: '#666666',
                            },
                        }
                        : {
                            // Dark mode colors — background/text matches zakovat-mobile's dark palette
                            primary: {
                                main: '#35a8ae',
                                light: '#5bbbc1',
                                dark: '#027a80',
                                contrastText: '#ffffff',
                            },
                            background: {
                                default: '#0D1117',
                                paper: '#161B22',
                            },
                            text: {
                                primary: '#E6EDF3',
                                secondary: '#8B949E',
                            },
                        }),
                },
                components: {
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                            },
                        },
                    },
                    MuiAccordion: {
                        styleOverrides: {
                            root: {
                                '&:before': {
                                    display: 'none',
                                },
                            },
                        },
                    },
                },
            }),
        [mode]
    );

    return (
        <ColorModeContext.Provider value={colorMode}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ColorModeContext.Provider>
    );
};
